import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Github } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1E293B] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-semibold mb-6">
              Peabsmart <span className="text-[#0EA5E9]">DevOps</span> Academy
            </h3>
            <p className="text-[#94A3B8] leading-relaxed mb-6">
              Transform your career with industry-leading DevOps training. 
              Master cloud infrastructure, automation, and modern deployment practices.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/company/peabsmart-consulting/?viewAsMember=true" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#334155] flex items-center justify-center hover:bg-[#0A66C2] transition-colors"
                aria-label="Visit our LinkedIn page"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#334155] flex items-center justify-center hover:bg-[#1DA1F2] transition-colors"
                aria-label="Follow us on Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://github.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#334155] flex items-center justify-center hover:bg-[#0EA5E9] transition-colors"
                aria-label="View our GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[15px] font-semibold mb-6 uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  All Courses
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Student Success Stories
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Courses */}
          <div>
            <h4 className="text-[15px] font-semibold mb-6 uppercase tracking-wider">Courses</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/courses/kubernetes" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Kubernetes Mastery
                </Link>
              </li>
              <li>
                <Link to="/courses/docker" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Docker & Containers
                </Link>
              </li>
              <li>
                <Link to="/courses/terraform" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Infrastructure as Code
                </Link>
              </li>
              <li>
                <Link to="/courses/cicd" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  CI/CD Pipelines
                </Link>
              </li>
              <li>
                <Link to="/courses/cloud" className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]">
                  Cloud Fundamentals
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[15px] font-semibold mb-6 uppercase tracking-wider">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                <div>
                  <a 
                    href="mailto:info@peabsmart.com" 
                    className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
                  >
                    info@peabsmart.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <a 
                    href="tel:+31626195070" 
                    className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
                    aria-label="Call Netherlands office"
                  >
                    +31 626 195 070
                  </a>
                  <a 
                    href="tel:+37061419646" 
                    className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
                    aria-label="Call Lithuania office"
                  >
                    +370 614 196 46
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[#94A3B8] text-[14px]">
                    Netherlands & Lithuania<br />
                    European Union
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#334155]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#94A3B8] text-[14px]">
              © {currentYear} Peabsmart DevOps Academy. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link 
                to="/privacy" 
                className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
              >
                Terms of Service
              </Link>
              <Link 
                to="/cookies" 
                className="text-[#94A3B8] hover:text-[#0EA5E9] transition-colors text-[14px]"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
